import { axios } from '@util'

class InvoicingService {

    // ── Draft Invoices ────────────────────────────────────────────────────────

    // `signal`: an AbortSignal that cancels the request (see BaseDataTableTable.vue).
    async getDraftInvoices(params = {}, { signal } = {}) {
        return await axios({
            url: '/invoicing/drafts/',
            method: 'GET',
            params,
            signal,
        })
    }

    async getDraftInvoiceDetail(params = {}) {
        return await axios({
            url: '/invoicing/drafts/detail/',
            method: 'GET',
            params,
        })
    }

    // ── Approval ──────────────────────────────────────────────────────────────

    async approveInvoice(data = {}) {
        return await axios({
            url: '/invoicing/approve/',
            method: 'POST',
            data,
        })
    }

    async bulkApproveInvoices(invoices = []) {
        return await axios({
            url: '/invoicing/approve/bulk/',
            method: 'POST',
            data: { invoices },
        })
    }

    // ── Approved / Dispatched Invoices ───────────────────────────────────────

    // `signal`: an AbortSignal that cancels the request (see BaseDataTableTable.vue).
    async getApprovedInvoices(params = {}, { signal } = {}) {
        return await axios({
            url: '/invoicing/approved/',
            method: 'GET',
            params,
            signal,
        })
    }

    async getInvoiceDetail(params = {}) {
        return await axios({
            url: '/invoicing/detail/',
            method: 'GET',
            params,
        })
    }

    // Same endpoint/params as getApprovedInvoices (current filter/search/
    // sort carries straight through) — export=xlsx switches the backend
    // response from JSON to a full (unpaginated) Excel file. responseType
    // 'blob' since this is real file content, not JSON.
    async exportApprovedInvoicesXlsx(params = {}) {
        return await axios({
            url: '/invoicing/approved/',
            method: 'GET',
            params: { ...params, export: 'xlsx' },
            responseType: 'blob',
        })
    }

    // Flat feed of InvoiceCharge rows for approved-and-dispatched invoices
    // — backs the Invoicing tab's Itemized view and Invoice view accordion.
    // LIQ-2656: values for the Invoicing page's multi-select column filters.
    async getFilterOptions() {
        return await axios({
            url: '/invoicing/filter-options/',
            method: 'GET',
        })
    }

    // `signal`: an AbortSignal that cancels the request (see BaseDataTableTable.vue).
    async getInvoiceCharges(params = {}, { signal } = {}) {
        return await axios({
            url: '/invoicing/charges/',
            method: 'GET',
            params,
            signal,
        })
    }

    // Same endpoint/params as getInvoiceCharges — see
    // exportApprovedInvoicesXlsx above for the export=xlsx/blob convention.
    async exportInvoiceChargesXlsx(params = {}) {
        return await axios({
            url: '/invoicing/charges/',
            method: 'GET',
            params: { ...params, export: 'xlsx' },
            responseType: 'blob',
        })
    }

    // ── Invoicing tab selection + actions (LIQ-2071/2072/2073/2465) ───────────

    // Every invoice matching the current filter/search, as compact
    // {invoice_id, invoice_status, is_archived, ...} rows — what "select
    // all" uses to select across every page and still know which actions
    // each selected row allows.
    async getInvoiceSelection(params = {}) {
        return await axios({
            skipToast: true,
            url: '/invoicing/approved/',
            method: 'GET',
            params: { ...params, selection: true },
        })
    }

    // Itemized-view equivalent of getInvoiceSelection — {charge_id,
    // invoice_id, received} rows.
    async getChargeSelection(params = {}) {
        return await axios({
            skipToast: true,
            url: '/invoicing/charges/',
            method: 'GET',
            params: { ...params, selection: true },
        })
    }

    // Itemized view's Download XLS for a selection — same columns as
    // exportInvoiceChargesXlsx, just the selected charges. POST so a large
    // selection's ids stay out of the query string.
    async exportSelectedChargesXlsx(params = {}, chargeIds = []) {
        return await axios({
            skipToast: true,
            url: '/invoicing/charges/',
            method: 'POST',
            data: { params, charge_ids: chargeIds },
            responseType: 'blob',
        })
    }

    // Stored PDF/XLS for the selected invoices — the file itself for one,
    // a .zip for several.
    async downloadInvoiceDocuments({ invoiceIds, docType, includeArchived = false }) {
        return await axios({
            skipToast: true,
            url: '/invoicing/documents/bulk-download/',
            method: 'POST',
            data: { invoice_ids: invoiceIds, doc_type: docType, include_archived: includeArchived },
            responseType: 'blob',
        })
    }

    // Archived invoices can't be resent — the proxy never includes them.
    async resendInvoices({ invoiceIds }) {
        return await axios({
            skipToast: true,
            url: '/invoicing/resend/',
            method: 'POST',
            data: { invoice_ids: invoiceIds },
        })
    }

    async archiveInvoices(invoiceIds = [], archive = true) {
        return await axios({
            skipToast: true,
            url: '/invoicing/archive/',
            method: 'POST',
            data: { invoice_ids: invoiceIds, archive },
        })
    }

    async getInvoicePayments(invoiceId) {
        return await axios({
            skipToast: true,
            url: '/invoicing/payments/',
            method: 'GET',
            params: { invoice_id: invoiceId },
        })
    }

    async addInvoicePayment(data = {}) {
        return await axios({
            skipToast: true,
            url: '/invoicing/payments/',
            method: 'POST',
            data,
        })
    }

    // LIQ-2074: correct a recorded payment in place (Lincoln audits the edit).
    async editInvoicePayment(data = {}) {
        return await axios({
            skipToast: true,
            url: '/invoicing/payments/',
            method: 'PATCH',
            data,
        })
    }

    async markChargesReceived(chargeIds = [], received = true) {
        return await axios({
            skipToast: true,
            url: '/invoicing/charges/received/',
            method: 'POST',
            data: { charge_ids: chargeIds, received },
        })
    }

    // Multipart upload — the frontend generates the PDF/XLS client-side
    // (useInvoiceExport.js) and hands it here for archival in Azure Blob
    // Storage (see Lincoln's InvoiceDocumentUploadView).
    async uploadInvoiceDocument(formData) {
        return await axios({
            url: '/invoicing/documents/upload/',
            method: 'POST',
            data: formData,
            headers: { 'Content-Type': 'multipart/form-data' },
        })
    }

    // responseType 'blob' — this returns real binary content (a stored
    // PDF/XLS), not JSON, so the caller can trigger a browser download.
    async downloadInvoiceDocument(params = {}) {
        return await axios({
            url: '/invoicing/documents/download/',
            method: 'GET',
            params,
            responseType: 'blob',
        })
    }

    async dispatchInvoice(data = {}) {
        return await axios({
            url: '/invoicing/dispatch/',
            method: 'POST',
            data,
        })
    }

    async returnToDraft(data = {}) {
        return await axios({
            url: '/invoicing/return-to-draft/',
            method: 'POST',
            data,
        })
    }

    // ── Summary cards ─────────────────────────────────────────────────────────

    async getInvoiceSummary(params = {}) {
        return await axios({
            url: '/invoicing/summary/',
            method: 'GET',
            params,
        })
    }

    // ── Reapprove (changed after approval) ───────────────────────────────────

    async reapproveInvoice(data = {}) {
        return await axios({
            url: '/invoicing/reapprove/',
            method: 'POST',
            data,
        })
    }

    // ── Header/page alerts ────────────────────────────────────────────────────

    async getInvoiceAlerts() {
        return await axios({
            url: '/invoicing/alerts/',
            method: 'GET',
        })
    }

    // ── Reference generation (client-side) ───────────────────────────────────
    // Market format:  LIQ-{YY}{MM}{C}-{TPA_PREFIX}{TPA_ID}-{UW_PREFIX}  (≤20 chars)
    // LIMOSS format:  LIQ-{YY}{MM}{C}-LMS-{UW_PREFIX}  (no TPA — LIMOSS is
    // invoiced per underwriter only, it has no TPA to derive a segment from)
    //            or:  LIQ-{YY}{MM}{C}-LMS-{SYNDICATE}  when the group has a
    // syndicate. LIMOSS is invoiced per syndicate, so one underwriter's two
    // syndicates are two invoices in the same period and need two references
    // (invoice_reference is unique). The syndicate stands in for the
    // underwriter prefix rather than beside it, to stay within 20 chars.

    generateReference(billingYear, billingMonth, currencyType, tpaName, tpaCompanyId, underwriterName, syndicateNumber) {
        const yy        = String(billingYear).slice(-2)
        const mm        = String(billingMonth).padStart(2, '0')
        const c         = (currencyType || 'X')[0].toUpperCase()
        const uwPrefix  = (underwriterName || '').replace(/\s/g, '').slice(0, 3).toUpperCase()

        if (!tpaCompanyId) {
            const party = syndicateNumber ? String(syndicateNumber).replace(/\s/g, '') : uwPrefix
            return `LIQ-${yy}${mm}${c}-LMS-${party}`.slice(0, 20)
        }

        const tpaPrefix = (tpaName || '').replace(/\s/g, '').slice(0, 3).toUpperCase()
        const tpaId     = String(tpaCompanyId || '').slice(0, 5)
        return `LIQ-${yy}${mm}${c}-${tpaPrefix}${tpaId}-${uwPrefix}`.slice(0, 20)
    }

}

export default new InvoicingService()